// Widget catalogue + dashboard layout model. The persistence wire format
// (SavedDashboard.layout) is a freeform JSON blob — see layoutAdapter for the
// shape-to-wire translation.

export const WIDGET_TYPES = [
  "kpi_total_pnl",
  "kpi_win_rate",
  "kpi_profit_factor",
  "kpi_avg_pnl",
  "chart_wins_losses",
  "chart_equity_curve",
  "chart_daily_pnl",
  "chart_drawdown",
  "panel_trades",
  "panel_calendar",
  "panel_streak",
] as const;

export type WidgetType = (typeof WIDGET_TYPES)[number];

export const WIDGET_CATEGORIES = [
  "Performance",
  "Risk",
  "Activity",
  "Charts",
] as const;

export type WidgetCategory = (typeof WIDGET_CATEGORIES)[number];

export type WidgetDefinition = {
  type: WidgetType;
  title: string;
  category: WidgetCategory;
  defaultSize: { w: number; h: number };
  minSize: { w: number; h: number };
};

export const WIDGET_DEFINITIONS: Record<WidgetType, WidgetDefinition> = {
  kpi_total_pnl: {
    type: "kpi_total_pnl",
    title: "Net P&L",
    category: "Performance",
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
  },
  kpi_win_rate: {
    type: "kpi_win_rate",
    title: "Win Rate",
    category: "Performance",
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
  },
  kpi_profit_factor: {
    type: "kpi_profit_factor",
    title: "Profit Factor",
    category: "Performance",
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
  },
  kpi_avg_pnl: {
    type: "kpi_avg_pnl",
    title: "Avg P&L / trade",
    category: "Performance",
    defaultSize: { w: 3, h: 3 },
    minSize: { w: 2, h: 2 },
  },
  chart_wins_losses: {
    type: "chart_wins_losses",
    title: "Daily Wins/Losses",
    category: "Charts",
    defaultSize: { w: 3, h: 4 },
    minSize: { w: 3, h: 3 },
  },
  chart_equity_curve: {
    type: "chart_equity_curve",
    title: "Cumulative P&L",
    category: "Charts",
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 3, h: 3 },
  },
  chart_daily_pnl: {
    type: "chart_daily_pnl",
    title: "Daily Net P&L",
    category: "Charts",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
  },
  chart_drawdown: {
    type: "chart_drawdown",
    title: "Drawdown",
    category: "Risk",
    defaultSize: { w: 3, h: 4 },
    minSize: { w: 3, h: 3 },
  },
  panel_trades: {
    type: "panel_trades",
    title: "Trades",
    category: "Activity",
    defaultSize: { w: 5, h: 8 },
    minSize: { w: 4, h: 4 },
  },
  panel_calendar: {
    type: "panel_calendar",
    title: "Calendar",
    category: "Activity",
    defaultSize: { w: 7, h: 8 },
    minSize: { w: 3, h: 5 },
  },
  panel_streak: {
    type: "panel_streak",
    title: "Last 14 Sessions",
    category: "Activity",
    defaultSize: { w: 6, h: 3 },
    minSize: { w: 3, h: 3 },
  },
};

export type WidgetInstance = {
  id: string;
  type: WidgetType;
  x: number;
  y: number;
  w: number;
  h: number;
};

export type DashboardLayout = {
  version: 2;
  widgets: WidgetInstance[];
};

// Default "Bento" dashboard (WickScope design): a large Cumulative P&L hero
// top-left; Net P&L + Win rate KPIs with Daily Net P&L stacked beneath them on
// the right; a compact row of Profit factor + Avg/trade + Last-14-sessions; and
// Recent trades beside a roomy month calendar along the bottom. The remaining
// widgets (wins/losses, drawdown) are available via "Add widget".
export const DEFAULT_DASHBOARD_LAYOUT: DashboardLayout = {
  version: 2,
  widgets: [
    { id: "chart_equity_curve", type: "chart_equity_curve", x: 0, y: 0, w: 6, h: 6 },
    { id: "kpi_total_pnl", type: "kpi_total_pnl", x: 6, y: 0, w: 3, h: 3 },
    { id: "kpi_win_rate", type: "kpi_win_rate", x: 9, y: 0, w: 3, h: 3 },
    { id: "chart_daily_pnl", type: "chart_daily_pnl", x: 6, y: 3, w: 6, h: 3 },
    { id: "kpi_profit_factor", type: "kpi_profit_factor", x: 0, y: 6, w: 3, h: 3 },
    { id: "kpi_avg_pnl", type: "kpi_avg_pnl", x: 3, y: 6, w: 3, h: 3 },
    { id: "panel_streak", type: "panel_streak", x: 6, y: 6, w: 6, h: 3 },
    { id: "panel_trades", type: "panel_trades", x: 0, y: 9, w: 6, h: 8 },
    { id: "panel_calendar", type: "panel_calendar", x: 6, y: 9, w: 6, h: 8 },
  ],
};

export const GRID_COLS = 12;
export const GRID_ROW_HEIGHT = 48;

// Starter presets surfaced on the empty-state screen. Each value is a
// pure JSON layout definition — see DashboardLayout v2 above for the
// envelope. Picking a preset replaces the in-memory layout wholesale;
// the persistence path is the same writeLayoutToBlob round-trip as a
// hand-edited layout.
export type PresetName = "Day Trader" | "Swing" | "Investor";

export const DASHBOARD_PRESETS: Record<PresetName, DashboardLayout> = {
  "Day Trader": {
    version: 2,
    widgets: [
      { id: "kpi_total_pnl", type: "kpi_total_pnl", x: 0, y: 0, w: 3, h: 2 },
      { id: "kpi_win_rate", type: "kpi_win_rate", x: 3, y: 0, w: 3, h: 2 },
      { id: "kpi_profit_factor", type: "kpi_profit_factor", x: 6, y: 0, w: 3, h: 2 },
      { id: "kpi_avg_pnl", type: "kpi_avg_pnl", x: 9, y: 0, w: 3, h: 2 },
      { id: "chart_daily_pnl", type: "chart_daily_pnl", x: 0, y: 2, w: 8, h: 4 },
      { id: "chart_wins_losses", type: "chart_wins_losses", x: 8, y: 2, w: 4, h: 4 },
      { id: "panel_trades", type: "panel_trades", x: 0, y: 6, w: 7, h: 6 },
      { id: "panel_calendar", type: "panel_calendar", x: 7, y: 6, w: 5, h: 6 },
    ],
  },
  Swing: {
    version: 2,
    widgets: [
      { id: "kpi_total_pnl", type: "kpi_total_pnl", x: 0, y: 0, w: 4, h: 2 },
      { id: "kpi_profit_factor", type: "kpi_profit_factor", x: 4, y: 0, w: 4, h: 2 },
      { id: "kpi_avg_pnl", type: "kpi_avg_pnl", x: 8, y: 0, w: 4, h: 2 },
      { id: "chart_equity_curve", type: "chart_equity_curve", x: 0, y: 2, w: 6, h: 4 },
      { id: "chart_drawdown", type: "chart_drawdown", x: 6, y: 2, w: 6, h: 4 },
      { id: "panel_trades", type: "panel_trades", x: 0, y: 6, w: 12, h: 6 },
    ],
  },
  Investor: {
    version: 2,
    widgets: [
      { id: "kpi_total_pnl", type: "kpi_total_pnl", x: 0, y: 0, w: 6, h: 2 },
      { id: "kpi_win_rate", type: "kpi_win_rate", x: 6, y: 0, w: 6, h: 2 },
      { id: "chart_equity_curve", type: "chart_equity_curve", x: 0, y: 2, w: 12, h: 5 },
      { id: "panel_calendar", type: "panel_calendar", x: 0, y: 7, w: 12, h: 5 },
    ],
  },
};

export const PRESET_NAMES: ReadonlyArray<PresetName> = [
  "Day Trader",
  "Swing",
  "Investor",
];

export const PRESET_DESCRIPTIONS: Record<PresetName, string> = {
  "Day Trader":
    "Intraday focus: 4 KPIs, daily P&L and wins/losses charts, recent trades + month calendar.",
  Swing:
    "Multi-day focus: P&L + profit factor + avg P&L, equity and drawdown side-by-side, full trades panel.",
  Investor:
    "Long-horizon: total P&L + win rate, large equity curve, full calendar view.",
};

export const EMPTY_LAYOUT: DashboardLayout = { version: 2, widgets: [] };
